<script setup lang="ts">
import { useLazyAsyncData, useNuxtApp } from "#app";
import { computed, onMounted, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import { clearAdsenseBlocks } from "~/utils/adsense";
import { useProductsMetaPageStore } from "~/stores/page/useProductsMetaPageStore";
import { useProductCollectionPageStore } from "~/stores/shop/useProductCollectionPageStore";

definePageMeta({
    alias: '/shop-page-:page',
});

const productsMetaPageStore = useProductsMetaPageStore();
const productCollectionPageStore = useProductCollectionPageStore();
const page = useRoute()?.params?.page !== undefined ? Number(useRoute()?.params.page) : 1;

await useLazyAsyncData('productsMetaPageStore', async () => productsMetaPageStore.fetch());
await useLazyAsyncData('productsByProductCollectionPageStore', async () => productCollectionPageStore.fetchList(page));
if (typeof productCollectionPageStore.error === "object") {
    throw productCollectionPageStore.error;
}

if (typeof productsMetaPageStore.error === "object") {
    throw productsMetaPageStore.error;
}

const textBeforeContent = computed(() => productsMetaPageStore.textBeforeContent ? useNuxtApp().marked().parse(productsMetaPageStore.textBeforeContent) : productsMetaPageStore.textBeforeContent);
const textAfterContent = computed(() => productsMetaPageStore.textAfterContent ? useNuxtApp().marked().parse(productsMetaPageStore.textAfterContent) : productsMetaPageStore.textAfterContent);
const products = computed(() => productCollectionPageStore.list);
const skeleton = computed(() => productCollectionPageStore.skeleton);
const error = computed(() => productCollectionPageStore.error);
const pagination = computed(() => productCollectionPageStore.pagination);

onMounted(() => {
    window.scrollTo(0, 0);
});

onUnmounted(() => {
    clearAdsenseBlocks();
    productCollectionPageStore.clear();
});
</script>

<template>
    <section class="text-gray-600 body-font container">
        <div class="md:flex md:flex-row">
            <aside class="mb-12 md:mb-initial md:w-2/5 lg:w-1/4 md:pr-8">
                <h2 class="hidden md:block sm:text-3xl text-2xl font-medium title-font mb-4 text-gray-900">Shop</h2>
                <NavAsideProductCategories/>
                <div class="hidden md:block space-y-6 mt-10">
                    <LastInterestingFactsByCategoryToAside :category-id="2" :page-size="4"/>
                    <ClientOnly>
                        <AdsenseMedia/>
                    </ClientOnly>
                    <LastInterestingFactsByCategoryToAside :category-id="4" :page-size="4"/>
                    <ClientOnly>
                        <AdsenseMedia/>
                    </ClientOnly>
                    <LastInterestingFactsByCategoryToAside :category-id="3" :page-size="4"/>
                    <ClientOnly>
                        <AdsenseMedia/>
                    </ClientOnly>
                    <LastInterestingFactsByCategoryToAside :category-id="1" :page-size="4"/>
                    <ClientOnly>
                        <AdsenseMedia/>
                    </ClientOnly>
                </div>
            </aside>

            <div class="md:w-3/5 lg:w-3/4 space-y-12">
                <div v-if="textBeforeContent !== undefined" class="prose w-full max-w-full leading-relaxed text-gray-500" v-html="textBeforeContent"></div>
                <div v-else>
                    <span class="animate-pulse block rounded h-4 w-[30%] bg-gray-100"></span>
                    <span class="animate-pulse block rounded h-4 w-[50%] bg-gray-100 mt-4"></span>
                    <span class="animate-pulse block rounded h-4 w-[40%] bg-gray-100 mt-4"></span>
                </div>

                <ClientOnly>
                    <section class="flex container justify-center">
                        <AdsenseSmallHorizontal/>
                    </section>
                </ClientOnly>
                <div v-if="products !== undefined && products.length > 0"
                     class="grid grid-cols-2 lg:grid-cols-3 gap-4"
                >
                    <CardShopProductPreview v-for="product in products"
                                :key="product.slug"
                                :title="product.title"
                                :sub-title="product.subTitle"
                                :picture-url="product.image"
                                :detail-url="product.url"
                                :buy-url="product.buyUrl"
                                :price="product.price"
                                :old-price="product.oldPrice"
                    />
                </div>
                <div v-else-if="products === undefined && skeleton === true && !error" class="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    <CardShopProductPreview v-for="index in 10" :key="index" :skeleton="true"/>
                </div>
                <div v-else-if="products !== undefined && !error && products.length <= 0">
                    <p class="text-lg leading-6 text-gray-900 font-bold">There are no recipes in this section yet. But soon there will be!</p>
                </div>
                <AlertErrorMessage v-else-if="error" :message="error" />
                <PaginationDefault v-if="pagination?.pageCount > 1"
                                   :page-count="pagination?.pageCount"
                                   :page="pagination?.page"
                                   base-path="/shop"
                />
                <ClientOnly>
                    <section class="hidden sm:flex container justify-center">
                        <AdsenseMediaBigWidth/>
                    </section>
                    <section class="sm:hidden container flex justify-center">
                        <AdsenseMedia/>
                    </section>
                </ClientOnly>
                <div v-if="textAfterContent !== undefined" class="prose w-full max-w-full leading-relaxed text-gray-500" v-html="textAfterContent"></div>
                <div v-else>
                    <span class="animate-pulse block rounded h-4 w-[30%] bg-gray-100"></span>
                    <span class="animate-pulse block rounded h-4 w-[50%] bg-gray-100 mt-4"></span>
                    <span class="animate-pulse block rounded h-4 w-[40%] bg-gray-100 mt-4"></span>
                </div>
            </div>
        </div>
    </section>
</template>